import React, { useRef } from 'react';
import { useCallback } from 'react';
import { useEffect } from 'react';
import { useState } from 'react';
import './custom.css';

interface CustomProps {
  id: string;
  index: number;
}

const COLUMN_COUNT = 32;
const ROW_COUNT = 32;
const INITIAL_GRID = Array.from({ length: ROW_COUNT }).map(() => Array.from({ length: COLUMN_COUNT }).map(() => ''));
const translations: Record<string, string> = {
  pt: 'Carregando...',
  en: 'Loading...'
}

const getInitialGrid  = () => [...INITIAL_GRID.map(row => [...row])];

const Custom: React.VFC<CustomProps> = ({ id, index }) => {
  const [color, setColor] = useState<string>('#000000');
  const [grid, setGrid] = useState<string[][]>(getInitialGrid());
  const [loading, setLoading] = useState<boolean>(true);
  const gridRef = useRef<string[][]>();

  useEffect(() => {
    gridRef.current = grid;
  }, [grid]);
  
  const fetchGrid = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetch('/.netlify/functions/get-interactive', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ index }),
      });

      const data = await result.json();

      if (data && data.data) {
        const parsed = JSON.parse(JSON.parse(data.data));

        setGrid(parsed.json);
      }

      setLoading(false);
    } catch {
      setLoading(false);
    }
  }, [index]);

  const save = useCallback(async () => {
    if (gridRef.current) {
      await fetch('/.netlify/functions/save-interactive', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id, index, json: gridRef.current }),
      });
    }
  }, [id, index]);

  useEffect(() => {
    setGrid(getInitialGrid());
  }, [id]);

  useEffect(() => {
    fetchGrid();

    return () => {
      save();
    };
  }, [fetchGrid, save]);

  return (
    <div>
      {id}
      <div className='paint-container'>
        {loading ? (
          <p>{translations[window.location.pathname.substring(1)] || translations['pt']}</p>
        ): (
          <>
            <Grid grid={grid} onGridChange={setGrid} color={color} />
            <Palette onColor={setColor} selected={color} />
          </>
        )}
      </div>
    </div>
  );
}

const Grid: React.VFC<{ grid: string[][]; onGridChange(grid: string[][]): void; color: string; }> = ({ grid, onGridChange, color }) => {
  const over = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, row: number, column: number) => {
    if (event.buttons === 1) {
      click(row, column);  
    }
  };
  const click = (row: number, column: number) => {
    grid[row][column] = color;
    onGridChange([...grid]);  
  }

  const renderColumns = (row: number) => {
    return Array.from({ length: COLUMN_COUNT }).map((_, column) => (
      <div
        key={`${row}-${column}`}
        style={{ backgroundColor: grid[row][column] }}
        onMouseOver={(e) => over(e, row, column)}
        onClick={() => click(row, column)}
        onMouseDown={() => click(row, column)}
      />
    ));
  };

  const renderRows = () => {
    return Array.from({ length: ROW_COUNT }).map((row, ri) => (renderColumns(ri)));
  };

  return (
    <div className='grid'>
      {renderRows()}
    </div>
  )
};

const Color: React.VFC<{ selected: boolean; color: string; onColor(color: string): void}> = ({ color, onColor, selected }) => {
  return (
    <div style={{ backgroundColor: color }} onClick={() => onColor(color)} className={selected ? 'selected' : undefined}></div>
  );
};

const colors = [
  '#000000','#333333','#4d4d4d',
  '#666666','#808080','#999999',
  '#b3b3b3','#cccccc','#ffffff',
  '#9f0500','#d33115','#f44e3b',
  '#c45100','#e27300','#fe9200',
  '#fb9e00','#fcc400','#fcdc00',
  '#808900','#b0bc00','#dbdf00',
  '#194d33','#68bc00','#a4dd00',
  '#0c797d','#16a5a5','#68ccca',
  '#0062b1','#009ce0','#73d8ff',
  '#653294','#7b64ff','#aea1ff',
  '#ab149e','#fa28ff','#fda1ff',
]

const Palette: React.VFC<{ selected: string; onColor(color: string): void}> = ({ onColor, selected }) => {
  return (
    <div className='palette'>
      {colors.map(color => (
        <Color key={color} color={color} onColor={onColor} selected={selected === color} />
      ))}
    </div>
  )
};

export default Custom;
